import type { Predication } from "@/types/Predication";

/**
 * Bibliothèque des prédications — valeurs initiales, modifiables depuis /admin
 * (voir src/content/). DONNÉES DE PRÉVISUALISATION.
 * Titres, résumés, dates et miniatures d'exemple ; à brancher sur la vraie
 * bibliothèque des prédications (voir data/media.ts pour la provenance
 * des images et de la vidéo). Seul « Timothée MBENGELA » est un vrai nom
 * (le pasteur principal, déjà utilisé ailleurs sur le site) — les autres
 * champs sont des exemples pour montrer le rendu de la recherche/des filtres.
 */
export const PREDICATIONS_INITIALES: Predication[] = [
  {
    id: "1",
    titre: "Joseph vit encore : La renaissance de l’espoir",
    // Résumé en Markdown (voir src/components/RichText.tsx) — titres, listes et emphases
    // s'écrivent avec la syntaxe Markdown standard (##, 1. / -, **gras**).
    resume: `## Introduction : de la tristesse à la vie réanimée

Le message du Pasteur Zachée Kabongo est un puissant baume d'espérance destiné à tous ceux qui traversent des saisons de sécheresse, d'obscurité ou de deuil spirituel. En s'appuyant sur la transition dramatique de la vie de Jacob et de son fils Joseph, le pasteur nous rappelle que, même lorsque tout semble perdu et que les circonstances crient à la mort, Dieu prépare en silence une glorieuse restauration. De même que l'esprit de Jacob s'est ranimé en découvrant que son fils bien-aimé régnait sur l'Égypte, Dieu veut aujourd'hui guérir nos blessures, ranimer notre foi et faire renaître notre espérance.

## Les 7 vérités clés à retenir

### 1. Ne jugez pas votre avenir à partir de votre présent

Jacob a pleuré Joseph pendant de nombreuses années, s'enfermant dans un deuil perpétuel et affirmant qu'il descendrait dans la tristesse au séjour des morts. Nous commettons souvent l'erreur de tirer des conclusions hâtives et incertaines basées sur notre présent limité. Les paroles et les analyses humaines sont incertaines ; seule la parole et la conclusion de Dieu sont certaines. Ne définissez pas votre avenir en fonction de vos difficultés d'aujourd'hui.

### 2. La vie ne se limite pas à ce que vous voyez

Ce que vous croyez mort, enterré ou définitivement perdu peut en réalité être en train de régner ailleurs pour préparer votre avenir. Joseph n'était pas mort sous les dents d'une bête féroce : il était en Égypte, élevé au rang de gouverneur, et préparait le terrain pour préserver la vie de toute sa famille. Vos « pertes » apparentes d'aujourd'hui cachent souvent les victoires de demain.

### 3. L'amour des hommes ne suffit pas

Bien que Jacob aimât profondément Joseph, cet amour paternel n'a pas pu protéger son fils de la haine et de la méchanceté de ses propres frères. L'amour humain, bien que nécessaire à notre équilibre et à notre estime personnelle, est limité et impuissant face aux complots de la vie. Nous avons besoin de l'amour parfait et souverain de Dieu pour sécuriser et accomplir notre destinée.

### 4. La haine des hommes n'est pas un obstacle à votre destinée

Les frères de Joseph ont comploté, l'ont jeté dans une citerne vide, l'ont dépouillé de sa tunique multicolore et l'ont vendu comme esclave. Pourtant, ils n'ont pas pu empêcher l'accomplissement de ses rêves. Deux choses ne pourront jamais vous être arrachées si vous êtes aimé de Dieu :

- Vos rêves (votre potentiel, vos talents, vos dons).
- Votre place dans le cœur du Père. Dieu voit les injustices, les rejets et les complots. Comme pour Léa (Genèse 29:31), Dieu est un Dieu de compassion qui voit notre souffrance et intervient pour compenser le manque d'amour et les rejets que nous subissons de la part des hommes.

### 5. Dieu travaille souvent en silence

Un arbre qui s'effondre fait beaucoup de bruit, mais une forêt entière qui pousse le fait dans un silence total. Le silence n'est pas de l'inactivité. Quand Dieu façonnait la destinée de Joseph en prison et dans la maison de Potifar, c'était dans le silence et la discrétion. Laissez Dieu faire Son œuvre de profondeur dans votre vie sans vous agiter.

### 6. Dieu n'a pas besoin de consulter les hommes pour vous visiter

Pour vous élever, vous consoler ou accomplir Ses promesses, Dieu ne consulte personne. Il ne demande la permission ni à vos partisans, ni à vos adversaires. Ce qui vous est destiné vous parviendra à coup sûr, peu importe le temps, les complots ou les oppositions.

### 7. Toutes choses concourent au bien de ceux qui aiment Dieu (Romains 8:28)

Chaque saison douloureuse, chaque trahison et chaque épreuve sont utilisées par la souveraineté divine pour concourir à notre bien et à Sa gloire. Au bout du processus, Dieu vous donnera non seulement la victoire, mais aussi la capacité d'aimer, de pardonner et de bénir ceux qui vous ont haï et qui ont tenté de vous détruire.

## Le message de restauration : la « salle de réanimation » de Dieu

À l'image du cœur de Jacob qui est resté « froid et insensible » lorsque ses fils lui ont annoncé que Joseph était vivant — parce qu'il n'arrivait plus à y croire —, beaucoup de croyants aujourd'hui ont le cœur anesthésié par la douleur et les déceptions. Ils sourient extérieurement mais saignent intérieurement, n'osant plus croire à l'amour, au bonheur ou à la guérison.

Mais la parole de Dieu ce matin est comme une eau fraîche pour une âme fatiguée (Proverbes 25:25). Dieu vous emmène dans Sa « salle de réanimation » spirituelle. Par Son Esprit, Il vient ranimer votre espérance, réactiver vos forces vitales et raviver la flamme de votre foi.

## Une attitude de foi pour avancer

Face aux défis de la vie, adoptons l'attitude de foi proclamée par le Pasteur Zachée Kabongo :

- Je crois en Dieu et en Sa fidélité.
- Je crois en moi et au potentiel qu'Il a mis en moi.
- Je n'ai pas peur du lendemain.
- Je décide de vivre, d'avancer et de réussir avec Dieu.

Que cette parole ranime vos cœurs et fortifie votre marche de foi ! Retrouvez l'intégralité de cette prédication sur notre chaîne YouTube pour être pleinement édifiés.`,
    predicateur: "Pasteur Zachée KABONGO",
    date: "2026-08-23",
    serie: "",
    videoId: "-v5X6jX1bdk",
  },
  {
    id: "2",
    titre: "Cherchez premièrement le royaume des cieux",
    resume:
      "La prière est la respiration de la vie chrétienne. Cette prédication explore comment une vie de prière régulière transforme notre relation avec Dieu et nous donne la force de traverser les épreuves du quotidien.",
    predicateur: "Pasteur Zachée KABONGO",
    date: "2026-08-16",
    serie: "",
    videoId: "bagu0VUuys0",
  },
  {
    id: "3",
    titre: "Qui sait si...",
    resume:
      "La grâce de Dieu n'est pas seulement un pardon reçu une fois, mais une réalité à vivre chaque jour. Ce message rappelle que nous sommes appelés à marcher dans la liberté et l'amour que la grâce nous offre, sans crainte ni condamnation.",
    predicateur: "Berger Christian NDJIAPANDA",
    date: "2026-08-09",
    serie: "",
    videoId: "aYSLPRbpbmc",
  },
  {
    id: "4",
    titre: "LA PUISSANCE DE LA RENCONTRE",
    resume:
      "Pardonner peut sembler impossible face à une blessure profonde. Cette prédication montre comment le pardon libère celui qui l'accorde autant que celui qui le reçoit, et pourquoi il est au cœur du message de l'Évangile.",
    predicateur: "Berger Christian NDJIAPANDA",
    date: "2026-08-02",
    serie: "",
    videoId: "J5nRsM-vsi8",
  },
];
